import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { IProgressIndicatorStyles} from '@fluentui/react';
import { mergeStyles, PrimaryButton, ProgressIndicator } from '@fluentui/react';
import { useNavigate } from 'react-router';
import PageBase from '../../components/PageBase/PageBase';
import PageTitle from '../../components/PageTitle/PageTitle';
import RouteConfig from '../../config/RouteConfig';
import Strings from '../../localization/strings';
import { FormContext } from '../../providers/FormProvider';
import RegisterActionHelper from '../../helpers/RegisterActionHelper';
import { getEwayObject, languageCountryCodeMap } from '../../helpers/ewayObjectHelper';
import pageImg from '../../img/graphic-design/eway_integrations-01.svg';
import gif1 from '../../img/finalization/1_install.gif';
import gif2 from '../../img/finalization/2_install.gif';
import gif3 from '../../img/finalization/3_install.gif';
import gif4 from '../../img/finalization/4_install.gif';
import gif5 from '../../img/finalization/5_install.gif';
import gif6 from '../../img/finalization/6_install.gif';
import gif7 from '../../img/finalization/7_install.gif';
import './FinalizationPage.scss';
import WebAccessPreloader from './WebAccessPreloader/WebAccessPreloader';
import { FinalizationSteps } from '../../constants/FinalizationSteps';
import useFinalizationState from './hooks/useFinalizationState';
import { FinalizationContext } from '../../providers/FinalizationProvider/FinalizationProvider';
import { LanguageContext } from '../../providers/LanguageProvider';
import { finalRedirect } from '../../helpers/FinalRedirect';
import { InlineWidget } from 'react-calendly';
import { Spinner, webLightTheme } from '@fluentui/react-components';
import { FluentProvider } from '@fluentui/react-components';
import { calendlyUrls, countriesDirectedToErnestCalendly, getIndustryGuidsForNewDesignPage } from '../../config/FinalizationPageConfig';

const PROGRESS_MESSAGE_WAIT_TIME = 4000;
const FADE_IN_CLASSNAME = 'finalization-page__page-img--fadein';
const FADE_OUT_CLASSNAME = 'finalization-page__page-img--fadeout';

export const progressIndicatorStyles: Partial<IProgressIndicatorStyles> = {
    itemName: {
        textAlign: 'center',
        fontSize: '1rem'
    },
    itemDescription: {
        textAlign: 'center',
        height: '4rem',
        fontSize: '14px'
    },
};

const useFinalizationProps = () => {
    const finalizationState = useFinalizationState();
    const { finalizationStep, hostingInfo, localDatabaseCreationInfo, progressMessage, } = finalizationState;

    const { registerAction } = useContext(FormContext);
    const isSignIn = RegisterActionHelper.isSignIn(registerAction);
    const eway = getEwayObject();
    const myStrings = Strings.finalization;
    const [lastHostingDaemonProgress, setLastHostingDaemonProgress] = useState({ stage: 0, updateTime: new Date() });
    const [finalPageImg, setFinalPageImg] = useState(isSignIn ? pageImg : gif1);
    const [finalPageImgClassName, setFinalPageImgClassName] = useState(FADE_IN_CLASSNAME);

    useEffect(() => {

        const changeImg = (newImg: string) => {
            setFinalPageImgClassName(FADE_OUT_CLASSNAME);
            setTimeout(() => {
                // Batch updates like this, because they are not batched until React 18+ (https://reactjs.org/blog/2022/03/08/react-18-upgrade-guide.html#automatic-batching)
                ReactDOM.unstable_batchedUpdates(() => {
                    setFinalPageImg(newImg);
                    setFinalPageImgClassName(FADE_IN_CLASSNAME);
                });
            }, 625);
        };

        if (finalizationState.wasUpdateTrialHostingStartedImmediatelly) {
            return;
        } else if (['cs', 'en', 'sk'].includes(Strings.getLanguage()) && !!hostingInfo) {
            if (finalizationStep === FinalizationSteps.CreateHosting || lastHostingDaemonProgress.stage !== hostingInfo.CreationCompleteStadiumNumber) {
                const FinalizationHostingDaemonStage24 = myStrings.finalizationHostingDaemonStage24;
                const hostingDaemonProgressProps = {
                    0: {
                        text: myStrings.finalizationHostingDaemonStage0,
                        img: gif1,
                    },
                    1: {
                        text: myStrings.finalizationHostingDaemonStage1,
                        img: gif2,
                    },
                    2: {
                        text: myStrings.finalizationHostingDaemonStage2,
                        img: gif3,
                    },
                    4: {
                        text: myStrings.finalizationHostingDaemonStage4,
                        img: gif4,
                    },
                    6: {
                        text: myStrings.finalizationHostingDaemonStage6,
                        img: gif5,
                    },
                    14: {
                        text: myStrings.finalizationHostingDaemonStage14,
                        img: gif6,
                    },
                    24: {
                        text: <FinalizationHostingDaemonStage24 />,
                        img: gif7,
                    },
                };

                let nextStageId = lastHostingDaemonProgress.stage;
                const currentStage = hostingInfo.CurrentCreationStadiumNumber;
                if (currentStage > lastHostingDaemonProgress.stage) {
                    nextStageId = Number(Object.keys(hostingDaemonProgressProps).find((stage) => Number(stage) > lastHostingDaemonProgress.stage) ?? 25);

                    const nextChangeTime = new Date(lastHostingDaemonProgress.updateTime.getTime() + PROGRESS_MESSAGE_WAIT_TIME);
                    const currentTime = new Date();
                    const canMakeNextChange = nextChangeTime < currentTime;

                    if (nextStageId <= currentStage && currentStage !== hostingInfo.CreationCompleteStadiumNumber && canMakeNextChange) {
                        setLastHostingDaemonProgress({ stage: nextStageId, updateTime: new Date() });

                        if (!isSignIn) {
                            changeImg(hostingDaemonProgressProps[nextStageId as keyof typeof hostingDaemonProgressProps].img);
                        }
                    } else if (currentStage === hostingInfo.CreationCompleteStadiumNumber) {
                        setLastHostingDaemonProgress({ stage: nextStageId, updateTime: new Date() });
                    }
                }
            } else if (finalizationStep === FinalizationSteps.CreateLocalDatabase) {

                setFinalPageImg(pageImg);
            }
        }
    }, [eway, finalizationStep, hostingInfo, lastHostingDaemonProgress, progressMessage, myStrings, localDatabaseCreationInfo, registerAction, isSignIn, finalizationState.wasUpdateTrialHostingStartedImmediatelly]);

    return { finalPageImg, finalPageImgClassName };
};

const FinalizationPage: React.FC = () => {
    const { registerAction, isLoggedViaMs, companyInfoTwoFormik, urlParams, introFormik, userInfoFormik, companyInfoFormik } = useContext(FormContext);
    const { language } = useContext(LanguageContext);
    const { finalization } = useContext(FinalizationContext);
    const finalizationState = useFinalizationState();
    const { error, finalizationStep, hostingInfo, accountsInfo } = finalizationState;

    const calendlyContainerRef = useRef<HTMLDivElement | null>(null);
    const finalizationPageRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== "https://calendly.com") return;

            const hasEventKey = (data: unknown): data is { event?: string; payload?: { height?: string } } => {
                return typeof data === 'object' && data !== null && 'event' in data && 'payload' in data;
            };

            const eventData: unknown = event?.data ?? {};
            if (!hasEventKey(eventData)) return;

            if (event.type === "message") {
                const calendlyWidget = document.querySelector('.calendly-inline-widget');
                if (eventData.event === "calendly.page_height") {
                    if (calendlyWidget) {
                        (calendlyWidget as HTMLElement).style.height = eventData.payload?.height ?? '';
                    }
                } else if (eventData.event === "calendly.event_scheduled") {
                    if (calendlyWidget) {
                        (calendlyWidget as HTMLElement).style.height = eventData.payload?.height ?? '';
                        (calendlyWidget as HTMLElement).style.maxWidth = "500px";
                    }

                    const calendlyContainer = calendlyContainerRef.current;
                    if (calendlyContainer) {
                        (calendlyContainer as HTMLElement).classList.add('finalization-page__calendly-container-scheduled');
                    }

                    const finalizationPageLeftSide = finalizationPageRef.current;
                    if (finalizationPageLeftSide) {
                        (finalizationPageLeftSide as HTMLElement).classList.add('finalization-page-new-scheduled');
                    }
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    const navigate = useNavigate();

    // Do not useValidatePreviousFormikOnMount on this page since we can come here after login without any info

    const eway = getEwayObject();

    const industriesForNewDesign = getIndustryGuidsForNewDesignPage();

    let calendlyUrl = "";
    if ((userInfoFormik.values.phoneDialCode?.key as string) === languageCountryCodeMap.cs) {
        calendlyUrl = calendlyUrls.cs;
    } else if (countriesDirectedToErnestCalendly.includes(userInfoFormik.values.phoneDialCode?.key as string)) {
        calendlyUrl = calendlyUrls.enErnest;
    } else {
        calendlyUrl = calendlyUrls.enJiri;
    }

    const isOldPageDesign = (() => {
        if (!userInfoFormik.values.phoneDialCode || !companyInfoFormik.values.industry || !companyInfoFormik.values.teamSize) {
            // The values are missing (for example after login in Desktop Outlook)
            return true;
        }

        return (userInfoFormik.values.phoneDialCode?.key === languageCountryCodeMap.sk
            || !industriesForNewDesign.includes(companyInfoFormik.values.industry.key as string) ||
            (industriesForNewDesign.includes(companyInfoFormik.values.industry.key as string) && companyInfoFormik.values.teamSize.text === '1')
        );
    })();

    useEffect(() => {
        if (error) {
            console.error(error);
            navigate(RouteConfig.otherPages.finalizationFailed);
            return;
        }

        if (finalizationStep !== FinalizationSteps.Finish) {
            return;
        }

        if (RegisterActionHelper.isSignIn(registerAction)) {
            navigate(RouteConfig.otherPages.loginSuccess);
        }
        else if (eway.isOutlookClient()) {
            navigate(RouteConfig.outlookPages.finishConfiguration);
        }
        else if (!hostingInfo || !accountsInfo) {
            console.error('Hosting info or account info is missing, could not navigate to WA Welcome page.');
            navigate(RouteConfig.otherPages.finalizationFailed);
        } else {
            if (isOldPageDesign) {
                finalRedirect(
                    urlParams,
                    hostingInfo,
                    accountsInfo,
                    companyInfoTwoFormik.values.outlookType,
                    isLoggedViaMs,
                    finalization?.connection?.apiConnection.sessionHandler.lastSuccessfulLoginResponse?.WcfVersion,
                    language,
                    () => navigate(RouteConfig.otherPages.finalizationFailed)
                );
            }
        }
    }, [navigate, eway, finalizationStep, accountsInfo, hostingInfo, registerAction, isLoggedViaMs, error, companyInfoTwoFormik.values.outlookType, finalization, language, urlParams, isOldPageDesign]);

    const { finalPageImg, finalPageImgClassName } = useFinalizationProps();

    const finished = finalizationStep === FinalizationSteps.Finish;
    const myStrings = Strings.finalization;

    const oldPageDesign = <PageBase imgSrc={finalPageImg} hideBackButton imgWrapperClassName={mergeStyles('finalization-page__page-img', finalPageImgClassName)}>
        <PageTitle title={Strings.finalization.title} subtitle={Strings.finalization.subtitle} />
        <div className="finalization-page">
            <ProgressIndicator label={myStrings.stayWithUs} description={myStrings.weAreCreatingYourAccount} className="finalization-page__progress" styles={progressIndicatorStyles} />
        </div>
        {(!!finalizationState.hostingInfo && !!finalizationState.hostingInfo.eWayWebServiceUrl && !!finalizationState.addTrialHostingProgressInfo?.IsComplete) &&
            <WebAccessPreloader hostingWebServiceUrl={finalizationState.hostingInfo.eWayWebServiceUrl} />
        }
    </PageBase>;

    const newPageDesign = <PageBase hideBackButton imgWrapperClassName={mergeStyles('finalization-page__page-img', finalPageImgClassName)} pageBaseBodyClassName="finalization-page__base-body" pageBaseClassName="finalization-page__base">
        <div ref={finalizationPageRef} className="finalization-page finalization-page-new" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
            <div style={{ flex: 2 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                    {finished ? (
                        <>
                            <span className="new-page-design-title">
                                <PageTitle title={Strings.finalization.finalizationFinishNewDesign.replace('eWay-CRM', 'eWay\u2011CRM')} />
                            </span>
                            <PrimaryButton className="page-base__next-btn rounded" onClick={() => {
                                if (hostingInfo && accountsInfo) {
                                    finalRedirect(
                                        urlParams,
                                        hostingInfo,
                                        accountsInfo,
                                        companyInfoTwoFormik.values.outlookType,
                                        isLoggedViaMs,
                                        finalization?.connection?.apiConnection.sessionHandler.lastSuccessfulLoginResponse?.WcfVersion,
                                        language,
                                        () => navigate(RouteConfig.otherPages.finalizationFailed)
                                    );
                                }
                            }}>
                                {myStrings.continueWithoutMeeting}
                            </PrimaryButton>
                        </>
                    ) : (
                        <>
                            <span className="new-page-design-title">
                                <PageTitle title={`${Strings.finalization.titleNewDesign} ${Strings.finalization.subtitleNewDesign}`} />
                            </span>

                            <FluentProvider theme={webLightTheme} style={{ backgroundColor: 'transparent' }} >
                                <Spinner size='huge' />
                            </FluentProvider>
                            <p className='finalization-page-new__spinner-message'>{myStrings.stayWithUs} {myStrings.weAreCreatingYourAccount}</p>

                            {(!!finalizationState.hostingInfo && !!finalizationState.hostingInfo.eWayWebServiceUrl && !!finalizationState.addTrialHostingProgressInfo?.IsComplete) &&
                                <WebAccessPreloader hostingWebServiceUrl={finalizationState.hostingInfo.eWayWebServiceUrl} />
                            }
                        </>
                    )}
                </div>
            </div>

            <div className="finalization-page__calendly-container" ref={calendlyContainerRef}>
                <InlineWidget
                    url={calendlyUrl}
                    prefill={{
                        name: introFormik.values.firstName + ' ' + introFormik.values.lastName,
                        email: introFormik.values.email
                    }}
                />
            </div>

        </div>
    </PageBase>;

    return isOldPageDesign ? oldPageDesign : newPageDesign;

};

export default FinalizationPage;
